import React, { useEffect, useState } from "react";
import {
  Alert, AppBar, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Divider,
  Grid, IconButton, List, ListItemButton, ListItemText, MenuItem, Paper, Slider,
  Stack, TextField, Toolbar, Typography,
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import AttachFileRounded from "@mui/icons-material/AttachFileRounded";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { adminService, directoryService, gigService, messageService, pageService, profileService, studioService, venueService } from "../services/resources.service";
import { formatDateTime, getMessage, useLocale, useMessage } from "../i18n/locale";
import { isPendingEventInvitation, normalizeMessages, normalizeThreads } from "../services/messaging";
import DateTimeField from "../components/DateTimeField";
import CitySelect from "../components/CitySelect";
import ProFeatureControl from "../components/ProFeatureControl";
import MultiTagField from "../components/MultiTagField";
import LocationFineTuner from "../components/LocationFineTuner";
import LoadingSpinner from "../components/LoadingSpinner";
import { validCoordinates } from "../components/locationFineTuner.utils";
import { hasProAccount } from "../services/proFeatures";
import { normalizeWebsiteUrl } from "../services/websiteUrl";
import AvailabilityEditor from "../components/AvailabilityEditor";
import { normalizeAvailability, validateAvailability } from "../services/availability";
import { validateEventDates } from "../services/eventDates";
import ImageUploadField from "../components/ImageUploadField";
import { normalizeImageValue } from "../services/imageInput";

function imageMessages(locale) {
  return {
    hint: getMessage(locale, "imageInputHint"),
    upload: getMessage(locale, "imageInputUpload"),
    addUrl: getMessage(locale, "imageInputAddUrl"),
    invalid: getMessage(locale, "imageInputInvalid"),
    invalidFile: getMessage(locale, "imageInputInvalidFile"),
    preview: getMessage(locale, "imageInputPreview"),
    remove: getMessage(locale, "imageInputRemove"),
  };
}

function PageHeader({ title, eyebrow, action }) {
  const navigate = useNavigate();
  return (
    <AppBar position="sticky">
      <Toolbar sx={{ maxWidth: 1200, width: "100%", mx: "auto", gap: 1.5 }}>
        <IconButton onClick={() => navigate(-1)} aria-label="Go back"><ArrowBackRounded /></IconButton>
        <Box sx={{ flex: 1 }}>
          {eyebrow && <Typography variant="overline" color="primary">{eyebrow}</Typography>}
          <Typography variant="h5">{title}</Typography>
        </Box>
        {action}
      </Toolbar>
    </AppBar>
  );
}

function FormShell({ children, aside }) {
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: aside ? 8 : 12 }}>{children}</Grid>
        {aside && <Grid size={{ xs: 12, md: 4 }}>{aside}</Grid>}
      </Grid>
    </Container>
  );
}

function RepeatableSection({ title, addLabel, emptyLabel, removeLabel, items, onChange, createItem, children }) {
  const updateItem = (index, key, value) => {
    onChange(items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  };
  const removeItem = (index) => onChange(items.filter((_item, itemIndex) => itemIndex !== index));
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Typography variant="h6">{title}</Typography>
        <Button type="button" size="small" variant="outlined" onClick={() => onChange([...items, createItem()])}>
          {addLabel}
        </Button>
      </Stack>
      {!items.length && <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>}
      {items.map((item, index) => (
        <Paper key={item.id || index} variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {children({ item, index, updateItem })}
            <Button type="button" size="small" color="error" sx={{ alignSelf: "flex-start" }} onClick={() => removeItem(index)}>
              {removeLabel}
            </Button>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function useSubmit(handler) {
  const [state, setState] = useState({ busy: false, error: "", done: false });
  const submit = async (payload) => {
    setState({ busy: true, error: "", done: false });
    try {
      const result = await handler(payload);
      setState({ busy: false, error: "", done: true });
      return result;
    } catch (error) {
      setState({ busy: false, error: error.message, done: false });
      return null;
    }
  };
  return [state, submit];
}

export function PostGig({ user }) {
  const locale = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const editing = Boolean(eventId);
  const venueId = new URLSearchParams(location.search).get("venueId") || "";
  const isPro = hasProAccount(user);
  const [form, setForm] = useState({
    title: "", description: "", date: "", endAt: "", doorsAt: "", applicationDeadline: "",
    city: locale.region, genres: "", budgetMin: "", budgetMax: "", currency: locale.currency,
    setLengthMinutes: "", backline: "", equipment: "", terms: "", ageLimit: "",
    capacity: "", parking: "", accessibility: "", ticketUrl: "", bannerUrl: "", attachments: [],
    invitedArtistIds: [],
  });
  const [gigId, setGigId] = useState("");
  const [venueCapacity, setVenueCapacity] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [artists, setArtists] = useState([]);
  const [artistsLoading, setArtistsLoading] = useState(true);
  const [artistsError, setArtistsError] = useState("");
  const [eventLoading, setEventLoading] = useState(editing);

  useEffect(() => {
    let active = true;
    directoryService.artists({ page: 1, pageSize: 100 })
      .then((response) => {
        if (!active) return;
        const availableArtists = Array.isArray(response) ? response : response?.data || response?.items || [];
        setArtists(availableArtists.filter((artist) => artist.ownerUserId));
        setArtistsError("");
      })
      .catch(() => active && setArtistsError(getMessage(locale.locale, "eventArtistInviteLoadError")))
      .finally(() => active && setArtistsLoading(false));
    return () => { active = false; };
  }, [locale.locale]);

  useEffect(() => {
    if (!editing) return;
    let active = true;
    gigService.forEvent(eventId)
      .then(async (gig) => {
        const linkedVenueId = gig.venue?.id;
        const venue = linkedVenueId ? await venueService.get(linkedVenueId) : null;
        return { gig, venue };
      })
      .then(({ gig, venue }) => {
        if (!active) return;
        const localDateTime = (value) => {
          if (!value) return "";
          const date = new Date(value);
          return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        };
        const parsedMaximum = Number(venue?.capacity);
        const maximum = Number.isInteger(parsedMaximum) && parsedMaximum > 0 ? parsedMaximum : null;
        setGigId(gig.id);
        setForm({
          title: gig.title || "", description: gig.description || "",
          date: localDateTime(gig.startAt || gig.date), endAt: localDateTime(gig.endAt),
          doorsAt: localDateTime(gig.doorsAt), applicationDeadline: gig.appDeadline || "",
          city: gig.venue?.city || locale.region, genres: (gig.genres || []).join(", "),
          budgetMin: gig.budgetMin ?? "", budgetMax: gig.budgetMax ?? "",
          currency: gig.currency || locale.currency, setLengthMinutes: gig.setLengthMinutes ?? "",
          backline: gig.backline || "", equipment: gig.equipment || "",
          terms: gig.paymentTerms || "", ageLimit: gig.ageLimit || "",
          capacity: gig.capacity ?? gig.eventCapacity ?? maximum ?? "",
          parking: gig.parking || "", accessibility: (gig.accessibility || []).join(", "),
          ticketUrl: gig.ticketUrl || "", bannerUrl: gig.bannerUrl || gig.poster || "",
          attachments: gig.attachments || [],
          invitedArtistIds: (gig.invitations || []).map((invitation) => invitation.artistId),
        });
        setVenueCapacity(maximum);
      })
      .catch((error) => active && setValidationError(error.message))
      .finally(() => active && setEventLoading(false));
    return () => { active = false; };
  }, [editing, eventId, locale.currency, locale.region]);

  useEffect(() => {
    if (editing || !venueId) return;
    let active = true;
    venueService.get(venueId)
      .then((venue) => {
        if (!active) return;
        const parsedMaximum = Number(venue.capacity);
        const maximum = Number.isInteger(parsedMaximum) && parsedMaximum > 0 ? parsedMaximum : null;
        setVenueCapacity(maximum);
        if (maximum !== null) {
          setForm((current) => ({ ...current, capacity: current.capacity || maximum }));
        }
      })
      .catch((error) => active && setValidationError(error.message));
    return () => { active = false; };
  }, [editing, venueId]);

  const [status, submit] = useSubmit(async (payload) => {
    const created = editing ? await gigService.update(gigId, payload) : await gigService.create(payload);
    const createdGigId = created?.gig?.id;
    const createdEventId = created?.event?.id;
    if (!createdGigId || !createdEventId || created.gig.eventId !== createdEventId) {
      throw new Error(getMessage(locale.locale, "gigPublishIncomplete"));
    }
    return created;
  });
  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const updateAttachments = (event) => {
    const files = Array.from(event.target.files || []);
    const allowedTypes = new Set(["application/pdf", "image/png", "image/jpeg"]);
    if (files.some((file) => !allowedTypes.has(file.type) || file.size > 10 * 1024 * 1024)) {
      setValidationError(getMessage(locale.locale, "gigAttachmentInvalid"));
      return;
    }
    setValidationError("");
    setForm((current) => ({
      ...current,
      attachments: files.map((file) => ({ name: file.name, size: file.size, type: file.type })),
    }));
  };

  return <>
    <PageHeader title={getMessage(locale.locale, editing ? "gigEditTitle" : "venueEventsPostGig")} eyebrow={getMessage(locale.locale, "gigVenueWorkspace")} />
    {eventLoading
      ? <LoadingSpinner minHeight="calc(100dvh - 64px)" size={44} />
      :
    <FormShell aside={
      <Card><CardContent>
        <Typography variant="h6" gutterBottom>{getMessage(locale.locale, "gigBriefTitle")}</Typography>
        <Typography color="text.secondary">{getMessage(locale.locale, "gigBriefHint")}</Typography>
        <Divider sx={{ my: 2 }} />
        <Chip color="secondary" label={form.currency} />
      </CardContent></Card>
    }>
      <Paper sx={{ p: { xs: 2.5, md: 4 } }}>
        <Stack component="form" autoComplete="on" spacing={2.5} onSubmit={async (e) => {
          e.preventDefault();
          const ticketUrl = isPro ? normalizeWebsiteUrl(form.ticketUrl) : "";
          if (ticketUrl === null) return;
          const bannerUrl = normalizeImageValue(form.bannerUrl);
          if (bannerUrl === null) {
            setValidationError(getMessage(locale.locale, "eventBannerUrlInvalid"));
            return;
          }
          const dateError = validateEventDates({
            startAt: form.date,
            endAt: form.endAt,
            doorsAt: form.doorsAt,
            applicationDeadline: form.applicationDeadline,
          });
          if (dateError) {
            setValidationError(getMessage(locale.locale, dateError));
            return;
          }
          const startAt = new Date(form.date);
          if (Number(form.budgetMin) > Number(form.budgetMax)) {
            setValidationError(getMessage(locale.locale, "gigBudgetInvalid"));
            return;
          }
          if (!Number.isInteger(Number(form.capacity)) || Number(form.capacity) < 1) {
            setValidationError(getMessage(locale.locale, "eventCapacityInvalid"));
            return;
          }
          if (venueCapacity !== null && Number(form.capacity) > venueCapacity) {
            setValidationError(getMessage(locale.locale, "eventCapacityExceedsVenue").replace("{capacity}", String(venueCapacity)));
            return;
          }
          setValidationError("");
          const created = await submit({
            venueId, title: form.title.trim(), description: form.description.trim(),
            ticketUrl, bannerUrl,
            startAt: startAt.toISOString(),
            endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
            doorsAt: form.doorsAt ? new Date(form.doorsAt).toISOString() : null,
            timezone: locale.timeZone,
            city: form.city,
            genres: form.genres.split(",").map((item) => item.trim()).filter(Boolean),
            budgetMinMinor: Math.round(Number(form.budgetMin) * 100),
            budgetMaxMinor: Math.round(Number(form.budgetMax) * 100),
            currency: form.currency, setLengthMinutes: Number(form.setLengthMinutes),
            backline: form.backline.trim(), equipment: form.equipment.trim(),
            terms: form.terms.trim(), ageLimit: form.ageLimit.trim(),
            capacity: Number(form.capacity),
            parking: form.parking.trim(),
            accessibility: form.accessibility.split(",").map((item) => item.trim()).filter(Boolean),
            applicationDeadline: form.applicationDeadline, attachments: form.attachments,
            invitedArtistIds: form.invitedArtistIds,
          });
          if (created) {
            navigate(`/events/${created.event.id}`, { state: { publishedGigId: created.gig.id, publishedEventId: created.event.id } });
          }
        }}>
          <Typography variant="h4">{getMessage(locale.locale, editing ? "gigEditHeading" : "gigCreateHeading")}</Typography>
          {validationError && <Alert severity="error">{validationError}</Alert>}
          {status.error && <Alert severity="error">{status.error}</Alert>}
          {status.done && <Alert severity="success">{getMessage(locale.locale, editing ? "gigUpdateSuccess" : "gigPublishSuccess")}</Alert>}
          <TextField required label={getMessage(locale.locale, "gigTitle")} value={form.title} onChange={update("title")} />
          <TextField required multiline minRows={4} label={getMessage(locale.locale, "gigDescription")} value={form.description} onChange={update("description")} />
          <ImageUploadField
            label={getMessage(locale.locale, "eventBannerUrlLabel")}
            value={form.bannerUrl}
            onChange={update("bannerUrl")}
            messages={imageMessages(locale.locale)}
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}><DateTimeField required fullWidth label={getMessage(locale.locale, "gigPerformanceStart")} type="datetime-local" value={form.date} onChange={update("date")} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><DateTimeField required fullWidth label={getMessage(locale.locale, "gigApplicationDeadline")} type="date" value={form.applicationDeadline} onChange={update("applicationDeadline")} slotProps={{ htmlInput: { max: form.date ? form.date.slice(0, 10) : undefined } }} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><DateTimeField fullWidth label={getMessage(locale.locale, "gigDoorsAt")} type="datetime-local" value={form.doorsAt} onChange={update("doorsAt")} slotProps={{ htmlInput: { max: form.date || undefined } }} /></Grid>
            <Grid size={{ xs: 12, sm: 6 }}><DateTimeField fullWidth label={getMessage(locale.locale, "gigEndAt")} type="datetime-local" value={form.endAt} onChange={update("endAt")} slotProps={{ htmlInput: { min: form.date || undefined } }} /></Grid>
          </Grid>
          <CitySelect required name="city" autoComplete="address-level2" value={form.city} onChange={update("city")} />
          <MultiTagField required label={getMessage(locale.locale, "gigGenres")} helperText={getMessage(locale.locale, "createPageTagEntryHint")} value={form.genres} onChange={update("genres")} />
          <Autocomplete
            multiple
            options={artists}
            loading={artistsLoading}
            value={artists.filter((artist) => form.invitedArtistIds.includes(artist.id))}
            onChange={(_event, selected) => setForm((current) => ({
              ...current,
              invitedArtistIds: selected.map((artist) => artist.id),
            }))}
            getOptionLabel={(artist) => artist.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={getMessage(locale.locale, "eventArtistInviteEmpty")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={getMessage(locale.locale, "eventArtistInviteLabel")}
                helperText={artistsError || getMessage(locale.locale, "eventArtistInviteHint")}
                error={Boolean(artistsError)}
              />
            )}
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}><TextField required fullWidth label={getMessage(locale.locale, "gigBudgetMin")} type="number" value={form.budgetMin} onChange={update("budgetMin")} /></Grid>
            <Grid size={{ xs: 12, sm: 4 }}><TextField required fullWidth label={getMessage(locale.locale, "gigBudgetMax")} type="number" value={form.budgetMax} onChange={update("budgetMax")} /></Grid>
            <Grid size={{ xs: 12, sm: 4 }}><TextField required select fullWidth label={getMessage(locale.locale, "gigCurrency")} value={form.currency} onChange={update("currency")}><MenuItem value="ZAR">ZAR</MenuItem><MenuItem value="USD">USD</MenuItem><MenuItem value="EUR">EUR</MenuItem><MenuItem value="GBP">GBP</MenuItem></TextField></Grid>
          </Grid>
          <TextField required type="number" label={getMessage(locale.locale, "gigSetLength")} value={form.setLengthMinutes} onChange={update("setLengthMinutes")} />
          <TextField required multiline minRows={2} label={getMessage(locale.locale, "gigBackline")} value={form.backline} onChange={update("backline")} />
          <TextField required multiline minRows={2} label={getMessage(locale.locale, "gigEquipment")} value={form.equipment} onChange={update("equipment")} />
          <TextField required multiline minRows={3} label={getMessage(locale.locale, "gigPaymentTerms")} value={form.terms} onChange={update("terms")} />
          <TextField label={getMessage(locale.locale, "gigAgeLimit")} value={form.ageLimit} onChange={update("ageLimit")} />
          <Box>
            <Typography gutterBottom>
              {getMessage(locale.locale, "eventCapacityLabel")}:{" "}
              {new Intl.NumberFormat(locale.locale, { maximumFractionDigits: 0 }).format(Number(form.capacity) || 1)}
            </Typography>
            <Slider
              disabled={venueCapacity === null}
              min={1}
              max={venueCapacity ?? 1}
              step={1}
              value={Math.min(Math.max(Number(form.capacity) || 1, 1), venueCapacity ?? 1)}
              valueLabelDisplay="auto"
              getAriaValueText={(value) => new Intl.NumberFormat(locale.locale, { maximumFractionDigits: 0 }).format(value)}
              aria-label={getMessage(locale.locale, "eventCapacityLabel")}
              onChange={(_event, value) => setForm((current) => ({ ...current, capacity: value }))}
            />
            <Typography variant="caption" color="text.secondary">
              {venueCapacity === null
                ? getMessage(locale.locale, "eventCapacityVenueUnknown")
                : getMessage(locale.locale, "eventCapacityVenueMaximum").replace(
                  "{capacity}",
                  new Intl.NumberFormat(locale.locale, { maximumFractionDigits: 0 }).format(venueCapacity),
                )}
            </Typography>
          </Box>
          <TextField multiline minRows={2} label={getMessage(locale.locale, "eventParkingLabel")} value={form.parking} onChange={update("parking")} />
          <MultiTagField
            label={getMessage(locale.locale, "eventAccessibilityLabel")}
            helperText={getMessage(locale.locale, "eventAccessibilityHint")}
            value={form.accessibility}
            onChange={update("accessibility")}
          />
          <Button component="label" variant="outlined" startIcon={<AttachFileRounded />}>
            {getMessage(locale.locale, "gigAttachments")}
            <input hidden multiple type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={updateAttachments} />
          </Button>
          {!!form.attachments.length && <Typography variant="body2">{form.attachments.map((item) => item.name).join(", ")}</Typography>}
          <ProFeatureControl
            locked={!isPro}
            label={getMessage(locale.locale, "eventTicketProviderLabel")}
          >
            <TextField
              type="text"
              inputMode="url"
              disabled={!isPro}
              label={getMessage(locale.locale, "eventTicketProviderLabel")}
              helperText={getMessage(
                locale.locale,
                isPro ? "eventTicketProviderHint" : "eventTicketProviderProHint",
              )}
              value={form.ticketUrl}
              onChange={update("ticketUrl")}
              sx={{ minWidth: { xs: 240, sm: 420 } }}
            />
          </ProFeatureControl>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={status.busy || (editing && !gigId)}
            startIcon={status.busy ? <CircularProgress size={20} color="inherit" aria-hidden="true" /> : undefined}
          >
            {getMessage(locale.locale, status.busy ? "gigSaving" : editing ? "gigSaveChanges" : "venueEventsPostGig")}
          </Button>
        </Stack>
      </Paper>
    </FormShell>}
  </>;
}

export function CreateBundle() {
  const locale = useLocale();
  const [form, setForm] = useState({ name: "", services: "", deliverables: "", price: "", currency: locale.currency, validUntil: "", terms: "" });
  const [status, submit] = useSubmit((value) => studioService.createBundle({ ...value, priceMinor: Math.round(Number(value.price) * 100) }));
  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  return <>
    <PageHeader title="Create a studio bundle" eyebrow="Studio workspace" />
    <FormShell>
      <Paper sx={{ p: { xs: 2.5, md: 4 }, maxWidth: 820, mx: "auto" }}>
        <Stack component="form" spacing={2.5} onSubmit={(e) => { e.preventDefault(); submit(form); }}>
          <Typography variant="h4">Package your sound</Typography>
          <Typography color="text.secondary">Combine services and deliverables into a clear, bookable offer.</Typography>
          {status.error && <Alert severity="error">{status.error}</Alert>}
          {status.done && <Alert severity="success">Bundle saved.</Alert>}
          <TextField required label="Bundle name" value={form.name} onChange={update("name")} />
          <TextField required label="Services" helperText="For example: tracking, editing, mix and master" value={form.services} onChange={update("services")} />
          <TextField multiline minRows={3} label="Deliverables" value={form.deliverables} onChange={update("deliverables")} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 8 }}><TextField required fullWidth type="number" label="Price" value={form.price} onChange={update("price")} /></Grid>
            <Grid size={{ xs: 4 }}><TextField select fullWidth label="Currency" value={form.currency} onChange={update("currency")}><MenuItem value="ZAR">ZAR</MenuItem><MenuItem value="USD">USD</MenuItem><MenuItem value="EUR">EUR</MenuItem></TextField></Grid>
          </Grid>
          <DateTimeField type="date" label="Valid until" value={form.validUntil} onChange={update("validUntil")} />
          <TextField multiline minRows={4} label="Terms" value={form.terms} onChange={update("terms")} />
          <Button variant="contained" type="submit" disabled={status.busy}>{status.busy ? "Saving…" : "Save bundle"}</Button>
        </Stack>
      </Paper>
    </FormShell>
  </>;
}

export function Inbox({ user, onUnreadCountChange }) {
  const loadingLabel = useMessage("loadingContent");
  const conversationError = useMessage("inboxConversationError");
  const messagesError = useMessage("inboxMessagesError");
  const searchLabel = useMessage("inboxSearch");
  const emptyThreadLabel = useMessage("inboxNoMessages");
  const organisationLabel = useMessage("inboxOrganisationConversation");
  const writeLabel = useMessage("inboxWriteMessage");
  const sendLabel = useMessage("inboxSendMessage");
  const refreshLabel = useMessage("inboxRefreshMessages");
  const backToConversationsLabel = useMessage("inboxBackToConversations");
  const invitationTitle = useMessage("inboxEventInvitationTitle");
  const invitationAccept = useMessage("inboxEventInvitationAccept");
  const invitationDecline = useMessage("inboxEventInvitationDecline");
  const invitationAccepted = useMessage("inboxEventInvitationAccepted");
  const invitationDeclined = useMessage("inboxEventInvitationDeclined");
  const invitationResponseError = useMessage("inboxEventInvitationResponseError");
  const locale = useLocale();
  const isPro = hasProAccount(user);
  const uploadLabel = useMessage("proUploadInboxFiles");
  const location = useLocation();
  const recipient = React.useMemo(() => {
    const query = new URLSearchParams(location.search);
    const type = query.get("recipientType");
    const id = query.get("recipientId");
    return type && id ? { recipientType: type, recipientId: id } : null;
  }, [location.search]);
  const [active, setActive] = useState(null);
  const [mobileConversationOpen, setMobileConversationOpen] = useState(Boolean(recipient));
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [body, setBody] = useState("");
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesErrorText, setMessagesErrorText] = useState("");
  const [threadError, setThreadError] = useState("");
  const [invitationBusy, setInvitationBusy] = useState("");
  const [invitationError, setInvitationError] = useState("");
  const [status, send] = useSubmit(async (message) => {
    const savedMessage = await messageService.send(active.id, { body: message });
    const normalized = normalizeMessages([savedMessage]);
    const nextMessage = normalized[0] || { id: `local-${Date.now()}`, mine: true, body: message };
    setMessages((items) => [...items, nextMessage]);
    setThreads((items) => items.map((thread) => (
      thread.id === active.id
        ? { ...thread, preview: nextMessage.body, updatedAt: nextMessage.sentAt || new Date().toISOString() }
        : thread
    )));
  });
  const sendMessage = () => {
    const message = body.trim();
    if (!active || !message || status.busy) return;
    setBody("");
    send(message);
  };
  useEffect(() => {
    let current = true;
    setThreadsLoading(true);
    Promise.allSettled([
      messageService.threads(),
      recipient ? messageService.startThread(recipient) : Promise.resolve(null),
    ])
      .then(([threadsResult, requestedThreadResult]) => {
        if (!current) return;
        const items = threadsResult.status === "fulfilled" ? threadsResult.value : [];
        const requestedThread = requestedThreadResult.status === "fulfilled"
          ? requestedThreadResult.value
          : null;
        const actualThreads = normalizeThreads(items);
        const matchingThread = requestedThread && (
          actualThreads.find((thread) => thread.id === requestedThread.id) || requestedThread
        );
        const nextThreads = matchingThread && !actualThreads.some((thread) => thread.id === matchingThread.id)
          ? [matchingThread, ...actualThreads]
          : actualThreads;
        setThreads(nextThreads);
        setActive(matchingThread || nextThreads[0] || null);
        setMobileConversationOpen(Boolean(matchingThread && recipient));
        const requestedConversationFailed = recipient && !matchingThread;
        const inboxFailed = !recipient && threadsResult.status === "rejected";
        setThreadError(requestedConversationFailed || inboxFailed ? conversationError : "");
      })
      .finally(() => {
        if (current) setThreadsLoading(false);
      });
    return () => { current = false; };
  }, [conversationError, recipient]);
  useEffect(() => {
    let current = true;
    setMessages([]);
    setMessagesErrorText("");
    if (!active?.id) {
      setMessagesLoading(false);
      return () => { current = false; };
    }
    setMessagesLoading(true);
    messageService.messages(active.id)
      .then((items) => {
        if (current) {
          setMessages(normalizeMessages(items));
          setThreads((currentThreads) => currentThreads.map((thread) => (
            thread.id === active.id ? { ...thread, unreadCount: 0 } : thread
          )));
          onUnreadCountChange?.();
        }
      })
      .catch(() => {
        if (current) setMessagesErrorText(messagesError);
      })
      .finally(() => {
        if (current) setMessagesLoading(false);
      });
    return () => { current = false; };
  }, [active?.id, messagesError, onUnreadCountChange]);
  const refreshMessages = async () => {
    if (!active?.id || messagesLoading) return;
    setMessagesLoading(true);
    setMessagesErrorText("");
    try {
      const items = await messageService.messages(active.id);
      setMessages(normalizeMessages(items));
      setThreads((currentThreads) => currentThreads.map((thread) => (
        thread.id === active.id ? { ...thread, unreadCount: 0 } : thread
      )));
      onUnreadCountChange?.();
    } catch {
      setMessagesErrorText(messagesError);
    } finally {
      setMessagesLoading(false);
    }
  };
  const respondToInvitation = async (message, responseStatus) => {
    if (!isPendingEventInvitation(message) || invitationBusy) return;
    setInvitationBusy(message.invitationId);
    setInvitationError("");
    try {
      await messageService.respondToInvitation(message.invitationId, responseStatus);
      await refreshMessages();
      onUnreadCountChange?.();
    } catch {
      setInvitationError(invitationResponseError);
    } finally {
      setInvitationBusy("");
    }
  };
  const threadSecondary = (thread) => {
    const preview = typeof thread.preview === "string" && thread.preview.trim()
      ? thread.preview.trim()
      : emptyThreadLabel;
    let timestamp = typeof thread.time === "string" ? thread.time.trim() : "";
    if (thread.updatedAt) {
      try {
        timestamp = formatDateTime(thread.updatedAt, locale);
      } catch {
        timestamp = "";
      }
    }
    return timestamp ? `${preview} · ${timestamp}` : preview;
  };
  return <>
    <PageHeader title="Inbox" eyebrow="Messages" />
    <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 3 }, py: { xs: 0, sm: 3 } }}>
      {threadError && <Alert severity="error" sx={{ mb: 2 }}>{threadError}</Alert>}
      <Paper sx={{ minHeight: { xs: "calc(100dvh - 64px)", sm: 620 }, overflow: "hidden", borderRadius: { xs: 0, sm: 2 } }}>
        <Grid container sx={{ minHeight: { xs: "calc(100dvh - 64px)", sm: 620 } }}>
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              display: { xs: mobileConversationOpen ? "none" : "block", md: "block" },
              borderInlineEnd: { md: 1 },
              borderColor: "divider",
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}><TextField fullWidth size="small" label={searchLabel} /></Box>
            {threadsLoading
              ? <Box sx={{ display: "grid", placeItems: "center", py: 4 }}><CircularProgress aria-label={loadingLabel} /></Box>
              : <List disablePadding>{threads.map((thread) => (
                <ListItemButton
                  key={thread.id}
                  selected={active?.id === thread.id}
                  onClick={() => {
                    setActive(thread);
                    setMobileConversationOpen(true);
                  }}
                  sx={{ minHeight: 72, px: { xs: 2, sm: 2.5 }, borderTop: 1, borderColor: "divider" }}
                >
                  <ListItemText
                    primary={thread.name || emptyThreadLabel}
                    secondary={threadSecondary(thread)}
                    slotProps={{
                      primary: { noWrap: true, fontWeight: thread.unreadCount ? 700 : 600 },
                      secondary: { noWrap: true },
                    }}
                  />
                </ListItemButton>
              ))}</List>}
          </Grid>
          <Grid
            size={{ xs: 12, md: 8 }}
            sx={{
              display: { xs: mobileConversationOpen ? "flex" : "none", md: "flex" },
              flexDirection: "column",
              minWidth: 0,
              minHeight: { xs: "calc(100dvh - 64px)", sm: 620 },
            }}
          >
            {active && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: { xs: 1, sm: 2.5 }, py: { xs: 1, sm: 2.5 }, borderBottom: 1, borderColor: "divider" }}>
                <IconButton
                  aria-label={backToConversationsLabel}
                  title={backToConversationsLabel}
                  onClick={() => setMobileConversationOpen(false)}
                  sx={{ display: { md: "none" } }}
                >
                  <ArrowBackRounded />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" noWrap>{active.name || emptyThreadLabel}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">{organisationLabel}</Typography>
                </Box>
                <IconButton
                  aria-label={refreshLabel}
                  title={refreshLabel}
                  disabled={messagesLoading}
                  onClick={refreshMessages}
                >
                  <RefreshRounded />
                </IconButton>
              </Stack>
            )}
            <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2.5 }, flex: 1, overflowY: "auto", minHeight: 0 }}>
              {messagesLoading
                ? <Box sx={{ display: "grid", flex: 1, placeItems: "center" }}><CircularProgress aria-label={loadingLabel} /></Box>
                : messages.map((message) => (
                  <Box key={message.id} sx={{ alignSelf: message.mine ? "flex-end" : "flex-start", maxWidth: { xs: "88%", sm: "75%" }, overflowWrap: "anywhere", bgcolor: message.mine ? "primary.main" : "background.default", color: message.mine ? "primary.contrastText" : "text.primary", px: { xs: 1.5, sm: 2 }, py: 1.25, borderRadius: 4 }}>
                    {message.kind === "EVENT_INVITATION" && <Typography variant="subtitle2">{invitationTitle}</Typography>}
                    <Typography variant="body2">{message.body}</Typography>
                    {message.kind === "EVENT_INVITATION" && message.invitationStatus !== "INVITED" && (
                      <Chip
                        size="small"
                        sx={{ mt: 1 }}
                        label={message.invitationStatus === "ACCEPTED" ? invitationAccepted : invitationDeclined}
                        color={message.invitationStatus === "ACCEPTED" ? "success" : "default"}
                      />
                    )}
                    {isPendingEventInvitation(message) && !message.mine && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                        <Button size="small" variant="contained" disabled={invitationBusy === message.invitationId} onClick={() => respondToInvitation(message, "ACCEPTED")}>
                          {invitationAccept}
                        </Button>
                        <Button size="small" variant="outlined" disabled={invitationBusy === message.invitationId} onClick={() => respondToInvitation(message, "DECLINED")}>
                          {invitationDecline}
                        </Button>
                      </Stack>
                    )}
                    {message.kind === "EVENT_INVITATION_RESPONSE" && (
                      <Typography variant="caption">
                        {message.responseStatus === "ACCEPTED" ? invitationAccepted : invitationDeclined}
                      </Typography>
                    )}
                  </Box>
                ))}
            </Stack>
            {messagesErrorText && <Alert severity="error">{messagesErrorText}</Alert>}
            {invitationError && <Alert severity="error">{invitationError}</Alert>}
            {status.error && <Alert severity="error">{status.error}</Alert>}
            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ p: { xs: 1, sm: 2 }, borderTop: 1, borderColor: "divider" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={writeLabel}
                    value={body}
                    disabled={!active}
                    slotProps={{ input: { readOnly: status.busy } }}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                  />
              </Box>
              <ProFeatureControl locked={!isPro} label={uploadLabel}>
                <IconButton aria-label={uploadLabel} disabled={!isPro || !active}><AttachFileRounded /></IconButton>
              </ProFeatureControl>
              <IconButton color="primary" aria-label={sendLabel} disabled={!active || !body.trim() || status.busy} onClick={sendMessage}><SendRounded /></IconButton>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  </>;
}

export function Admin() {
  const [reports, setReports] = useState([]);
  useEffect(() => {
    adminService.reports().then(setReports);
  }, []);
  const updateStatus = async (report, status) => {
    try { await adminService.updateReport(report.id, { status }); } catch {}
    setReports((items) => items.map((item) => item.id === report.id ? { ...item, status } : item));
  };
  return <>
    <PageHeader title="Moderation" eyebrow="Admin" />
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ["Open", "OPEN"],
          ["Investigating", "INVESTIGATING"],
          ["Resolved", "ACTIONED"],
        ].map(([label, status]) => <Grid key={status} size={{ xs: 12, sm: 4 }}><Card><CardContent><Typography color="text.secondary">{label}</Typography><Typography variant="h3">{reports.filter((report) => report.status === status).length}</Typography></CardContent></Card></Grid>)}
      </Grid>
      <Paper sx={{ overflow: "hidden" }}>
        <Box sx={{ p: 3 }}><Typography variant="h5">Report queue</Typography></Box>
        <Divider />
        {reports.map((report) => <Stack key={report.id} direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ sm: "center" }} sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ flex: 1 }}><Typography fontWeight={700}>#{report.id} · {report.subject}</Typography><Typography color="text.secondary">{report.type} · {report.reason}</Typography></Box>
          <Chip label={report.status} color={report.status === "OPEN" ? "primary" : "default"} />
          <Button onClick={() => updateStatus(report, "INVESTIGATING")}>Review</Button>
          <Button variant="contained" onClick={() => updateStatus(report, "ACTIONED")}>Resolve</Button>
        </Stack>)}
      </Paper>
    </Container>
  </>;
}

export function CreatePage({ kind = "artist" }) {
  const safeType = ["artist", "venue", "studio", "media"].includes(kind) ? kind : "artist";
  const { id } = useParams();
  const isEditing = Boolean(id);
  const locale = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const onboarding = location.state?.onboarding;
  const [form, setForm] = useState({
    name: location.state?.name || "",
    city: location.state?.city || locale.region,
    sourceLocale: locale.locale,
    bio: "",
    avatar: "",
    hero: "",
    photos: [],
    phone: "",
    siteUrl: "",
    links: { spotify: "", youtube: "", instagram: "", facebook: "", soundcloud: "" },
    address: "",
    geo: null,
    genres: safeType === "artist" && location.state?.primaryTag ? location.state.primaryTag : "",
    members: "",
    travelDistance: "",
    feeMin: "",
    feeMax: "",
    currency: locale.currency,
    capacity: "",
    budgetMin: "",
    budgetMax: "",
    facilities: safeType === "venue" && location.state?.primaryTag ? location.state.primaryTag : "",
    stageSpec: "",
    policies: "",
    services: (safeType === "studio" || safeType === "media") && location.state?.primaryTag ? location.state.primaryTag : "",
    gear: "",
    rooms: [],
    engineers: [],
    rateCards: [],
    portfolio: [],
    studioPolicies: "",
    timeZone: locale.timeZone,
    availability: [],
    techRider: null,
  });
  const [account, setAccount] = useState(null);
  const [status, setStatus] = useState({ busy: false, error: "" });
  const [loadingPage, setLoadingPage] = useState(isEditing);
  const [pageAccessible, setPageAccessible] = useState(!isEditing);
  const kindKey = `createPageKind${safeType[0].toUpperCase()}${safeType.slice(1)}`;
  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const updateCity = (event) => {
    const latitude = Number(event.city?.latitude);
    const longitude = Number(event.city?.longitude);
    setForm((current) => ({
      ...current,
      city: event.target.value,
      geo: Number.isFinite(latitude) && Number.isFinite(longitude)
        ? { lat: latitude, lng: longitude }
        : null,
    }));
  };
  const list = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);
  const optionalNumber = (value) => value === "" ? null : Number(value);
  useEffect(() => {
    if (isEditing) return;
    profileService.me().then(setAccount).catch(() => setAccount(null));
  }, [isEditing]);
  useEffect(() => {
    if (!isEditing) return;
    let current = true;
    profileService.me()
      .then((profile) => {
        if (!current) return;
        setAccount(profile);
        const pagesKey = `${safeType}Pages`;
        const page = (profile[pagesKey] || []).find((item) => String(item.id) === String(id));
        if (!page) {
          setPageAccessible(false);
          setStatus({ busy: false, error: getMessage(locale.locale, "editPageNotOwned") });
          return;
        }
        setPageAccessible(true);
        const listValue = (value) => Array.isArray(value) ? value.join(", ") : "";
        setForm((currentForm) => ({
          ...currentForm,
          ...page,
          links: { spotify: "", youtube: "", instagram: "", facebook: "", soundcloud: "", ...(page.links || {}) },
          photos: Array.isArray(page.photos) ? page.photos : [],
          genres: listValue(page.genres),
          members: listValue(page.members),
          facilities: listValue(page.facilities),
          services: listValue(page.services),
          gear: listValue(page.gear),
          rooms: Array.isArray(page.rooms)
            ? page.rooms.map((room) => typeof room === "string" ? { name: room, size: "", photo: "" } : room)
            : [],
          engineers: Array.isArray(page.engineers)
            ? page.engineers.map((engineer) => ({ ...engineer, instagram: engineer.instagram || engineer.links?.instagram || "" }))
            : [],
          rateCards: Array.isArray(page.rateCards) ? page.rateCards : [],
          portfolio: Array.isArray(page.portfolio) ? page.portfolio : [],
          studioPolicies: page.policies || "",
          timeZone: page.timeZone || page.timezone || locale.timeZone,
          availability: normalizeAvailability(page.availability, locale.timeZone),
          travelDistance: page.travelDistance ?? "",
          feeMin: page.feeMin ?? "",
          feeMax: page.feeMax ?? "",
          capacity: page.capacity ?? "",
          budgetMin: page.budgetMin ?? "",
          budgetMax: page.budgetMax ?? "",
        }));
      })
      .catch((error) => current && setStatus({ busy: false, error: error.message }))
      .finally(() => current && setLoadingPage(false));
    return () => { current = false; };
  }, [id, isEditing, locale.locale, locale.timeZone, safeType]);
  const isPro = hasProAccount(account);
  const selectTechRider = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf" || file.size > 10 * 1024 * 1024) {
      setStatus({ busy: false, error: getMessage(locale.locale, "techRiderInvalid") });
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setStatus({ busy: false, error: getMessage(locale.locale, "techRiderReadError") });
    reader.onload = () => setForm((current) => ({
      ...current,
      techRider: { name: file.name, type: file.type, size: file.size, url: reader.result },
    }));
    reader.readAsDataURL(file);
  };
  useEffect(() => {
    if (!loadingPage && location.hash === "#availability") {
      document.getElementById("availability")?.scrollIntoView({ block: "start" });
    }
  }, [loadingPage, location.hash]);
  const submit = async () => {
    setStatus({ busy: true, error: "" });
    try {
      const siteUrl = normalizeWebsiteUrl(form.siteUrl);
      if (siteUrl === null) {
        setStatus({ busy: false, error: getMessage(locale.locale, "createPageWebsiteInvalid") });
        return;
      }
      const socialLinks = Object.fromEntries(
        Object.entries(form.links || {}).map(([network, value]) => [network, normalizeWebsiteUrl(value)])
      );
      if (Object.values(socialLinks).some((value) => value === null)) {
        setStatus({ busy: false, error: getMessage(locale.locale, "createPageSocialInvalid") });
        return;
      }
      if (form.geo && !validCoordinates(form.geo)) {
        setStatus({ busy: false, error: getMessage(locale.locale, "editPageLocationInvalid") });
        return;
      }
      if (!validateAvailability(form.availability)) {
        setStatus({ busy: false, error: getMessage(locale.locale, "availabilityInvalid") });
        return;
      }
      if ((safeType === "studio" || safeType === "media") && !/^[A-Z]{3}$/.test(form.currency.trim().toUpperCase())) {
        setStatus({ busy: false, error: getMessage(locale.locale, "createPageCurrencyInvalid") });
        return;
      }
      if (safeType === "studio" || safeType === "media") {
        try {
          new Intl.DateTimeFormat(locale.locale, { timeZone: form.timeZone.trim() }).format();
        } catch {
          setStatus({ busy: false, error: getMessage(locale.locale, "createPageTimeZoneInvalid") });
          return;
        }
      }
      const payload = {
        ...form,
        techRider: isPro ? form.techRider : undefined,
        links: Object.fromEntries(Object.entries(socialLinks).filter(([, value]) => value)),
        geo: form.geo ? { lat: Number(form.geo.lat), lng: Number(form.geo.lng) } : null,
        siteUrl,
        photos: form.photos.filter(Boolean),
        genres: list(form.genres),
        members: list(form.members),
        facilities: list(form.facilities),
        services: list(form.services),
        gear: list(form.gear),
        rooms: form.rooms.map(({ name, size, photo }) => ({
          name: String(name || "").trim(), size: String(size || "").trim(), photo: String(photo || "").trim(),
        })).filter((room) => room.name),
        engineers: form.engineers.map((engineer, index) => ({
          id: engineer.id || `engineer-${index + 1}`,
          name: String(engineer.name || "").trim(),
          role: String(engineer.role || "").trim(),
          avatar: String(engineer.avatar || "").trim(),
          links: String(engineer.instagram || "").trim() ? { instagram: String(engineer.instagram).trim() } : {},
        })).filter((engineer) => engineer.name),
        rateCards: form.rateCards.map((rate) => ({
          label: String(rate.label || "").trim(), price: optionalNumber(rate.price),
        })).filter((rate) => rate.label && rate.price !== null),
        portfolio: form.portfolio.map((item) => ({
          type: item.type === "video" ? "video" : "audio",
          title: String(item.title || "").trim(), url: String(item.url || "").trim(), thumb: String(item.thumb || "").trim(),
        })).filter((item) => item.title && item.url),
        policies: form.studioPolicies.trim(),
        currency: form.currency.trim().toUpperCase(),
        timeZone: form.timeZone.trim(),
        travelDistance: optionalNumber(form.travelDistance),
        feeMin: optionalNumber(form.feeMin),
        feeMax: optionalNumber(form.feeMax),
        capacity: optionalNumber(form.capacity),
        budgetMin: optionalNumber(form.budgetMin),
        budgetMax: optionalNumber(form.budgetMax),
      };
      const page = isEditing
        ? await pageService.update(safeType, id, payload)
        : await pageService.create(safeType, payload);
      navigate(`/${safeType}s/${page.id}`, { replace: true });
    } catch (error) {
      setStatus({ busy: false, error: error.message });
    }
  };
  return <>
    <PageHeader
      title={getMessage(locale.locale, isEditing ? "editPageTitle" : "createPageTitle").replace("{type}", getMessage(locale.locale, kindKey))}
      eyebrow={getMessage(locale.locale, isEditing ? "editPageEyebrow" : "createPageEyebrow")}
    />
    <FormShell>
      <Paper sx={{ p: { xs: 2.5, md: 4 }, maxWidth: 760, mx: "auto" }}>
        {loadingPage ? <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}><CircularProgress /></Box> :
        !pageAccessible ? <Alert severity="error">{status.error}</Alert> :
        <Stack component="form" autoComplete="on" spacing={2.5} onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {status.error && <Alert severity="error">{status.error}</Alert>}
          {onboarding && <Alert severity="info">{getMessage(locale.locale, "createPageOnboarding")}</Alert>}
          <TextField required autoFocus label={getMessage(locale.locale, "createPageName")} value={form.name} onChange={update("name")} />
          {(safeType === "artist") && (
            <CitySelect required name="city" autoComplete="address-level2" label={getMessage(locale.locale, "createPageCity")} value={form.city} onChange={updateCity} />
          )}
          {(safeType === "venue" || safeType === "studio" || safeType === "media") && (
            <>
              <TextField
                name="address"
                autoComplete="street-address"
                label={getMessage(locale.locale, "createPageAddress")}
                value={form.address}
                onChange={update("address")}
              />
              <CitySelect required name="city" autoComplete="address-level2" label={getMessage(locale.locale, "createPageCity")} value={form.city} onChange={updateCity} />
            </>
          )}
          {isEditing && (
            <LocationFineTuner
              value={form.geo}
              onChange={(geo) => setForm((current) => ({ ...current, geo }))}
            />
          )}
          <TextField multiline minRows={5} label={getMessage(locale.locale, "createPageAbout")} value={form.bio} onChange={update("bio")} />
          <Typography variant="h6">{getMessage(locale.locale, "createPageMedia")}</Typography>
          <ImageUploadField label={getMessage(locale.locale, "createPageProfileImage")} value={form.avatar} onChange={update("avatar")} messages={imageMessages(locale.locale)} />
          <ImageUploadField label={getMessage(locale.locale, "createPageCoverImage")} value={form.hero} onChange={update("hero")} messages={imageMessages(locale.locale)} />
          <ImageUploadField multiple label={getMessage(locale.locale, "createPageGallery")} value={form.photos} onChange={update("photos")} messages={imageMessages(locale.locale)} />
          {safeType === "artist" && (
            <ProFeatureControl locked={!isPro} label={getMessage(locale.locale, "techRiderLabel")}>
              <Stack spacing={1}>
                <Typography variant="h6">{getMessage(locale.locale, "techRiderLabel")}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {getMessage(locale.locale, isPro ? "techRiderHint" : "techRiderProHint")}
                </Typography>
                {form.techRider?.name && (
                  <Chip label={form.techRider.name} onDelete={isPro ? () => setForm((current) => ({ ...current, techRider: null })) : undefined} />
                )}
                <Button component="label" variant="outlined" startIcon={<AttachFileRounded />} disabled={!isPro} sx={{ alignSelf: "flex-start" }}>
                  {getMessage(locale.locale, form.techRider ? "techRiderReplace" : "proUploadTechRider")}
                  <input hidden type="file" accept="application/pdf,.pdf" onChange={selectTechRider} />
                </Button>
              </Stack>
            </ProFeatureControl>
          )}
          <Typography variant="h6">{getMessage(locale.locale, "createPageContact")}</Typography>
          <TextField type="tel" label={getMessage(locale.locale, "createPagePhone")} value={form.phone} onChange={update("phone")} />
          <TextField
            type="text"
            inputMode="url"
            label={getMessage(locale.locale, "createPageWebsite")}
            helperText={getMessage(locale.locale, "createPageWebsiteHint")}
            value={form.siteUrl}
            onChange={update("siteUrl")}
            onBlur={() => {
              const siteUrl = normalizeWebsiteUrl(form.siteUrl);
              if (siteUrl !== null) setForm((current) => ({ ...current, siteUrl }));
            }}
          />
          {safeType === "artist" && (
              <Stack spacing={1.5}>
                <Typography variant="h6">{getMessage(locale.locale, "createPageSocialTitle")}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {getMessage(locale.locale, "createPageSocialHint")}
                </Typography>
                <Grid container spacing={1.5}>
                  {["spotify", "youtube", "instagram", "facebook", "soundcloud"].map((network) => (
                    <Grid key={network} size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        type="text"
                        inputMode="url"
                        label={getMessage(locale.locale, `createPageSocial${network[0].toUpperCase()}${network.slice(1)}`)}
                        value={form.links?.[network] || ""}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          links: { ...current.links, [network]: event.target.value },
                        }))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
          )}
          {safeType === "artist" && <>
            <Typography variant="h6">{getMessage(locale.locale, "createPageArtistDetails")}</Typography>
            <MultiTagField label={getMessage(locale.locale, "createPageGenres")} helperText={getMessage(locale.locale, "createPageTagEntryHint")} value={form.genres} onChange={update("genres")} />
            <MultiTagField label={getMessage(locale.locale, "createPageMembers")} helperText={getMessage(locale.locale, "createPageTagEntryHint")} value={form.members} onChange={update("members")} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth type="number" label={getMessage(locale.locale, "createPageTravelDistance")} value={form.travelDistance} onChange={update("travelDistance")} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth type="number" label={getMessage(locale.locale, "createPageFeeMin")} value={form.feeMin} onChange={update("feeMin")} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth type="number" label={getMessage(locale.locale, "createPageFeeMax")} value={form.feeMax} onChange={update("feeMax")} /></Grid>
            </Grid>
          </>}
          {safeType === "venue" && <>
            <Typography variant="h6">{getMessage(locale.locale, "createPageVenueDetails")}</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth type="number" label={getMessage(locale.locale, "createPageCapacity")} value={form.capacity} onChange={update("capacity")} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth type="number" label={getMessage(locale.locale, "createPageBudgetMin")} value={form.budgetMin} onChange={update("budgetMin")} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth type="number" label={getMessage(locale.locale, "createPageBudgetMax")} value={form.budgetMax} onChange={update("budgetMax")} /></Grid>
            </Grid>
            <MultiTagField label={getMessage(locale.locale, "createPageFacilities")} helperText={getMessage(locale.locale, "createPageTagEntryHint")} value={form.facilities} onChange={update("facilities")} />
            <TextField multiline minRows={3} label={getMessage(locale.locale, "createPageStageSpec")} value={form.stageSpec} onChange={update("stageSpec")} />
            <TextField multiline minRows={3} label={getMessage(locale.locale, "createPagePolicies")} value={form.policies} onChange={update("policies")} />
          </>}
          {(safeType === "studio" || safeType === "media") && <>
            <Typography variant="h6">{getMessage(locale.locale, safeType === "media" ? "createPageMediaDetails" : "createPageStudioDetails")}</Typography>
            <MultiTagField label={getMessage(locale.locale, "createPageServices")} helperText={getMessage(locale.locale, "createPageTagEntryHint")} value={form.services} onChange={update("services")} />
            <MultiTagField label={getMessage(locale.locale, "createPageGear")} helperText={getMessage(locale.locale, "createPageTagEntryHint")} value={form.gear} onChange={update("gear")} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField required fullWidth label={getMessage(locale.locale, "createPageCurrency")} value={form.currency} onChange={update("currency")} inputProps={{ maxLength: 3 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField required fullWidth label={getMessage(locale.locale, "createPageTimeZone")} helperText={getMessage(locale.locale, "createPageTimeZoneHint")} value={form.timeZone} onChange={update("timeZone")} />
              </Grid>
            </Grid>
            <RepeatableSection
              title={getMessage(locale.locale, "createPageRooms")}
              addLabel={getMessage(locale.locale, "createPageAddRoom")}
              emptyLabel={getMessage(locale.locale, "createPageRoomsEmpty")}
              removeLabel={getMessage(locale.locale, "createPageRemoveItem")}
              items={form.rooms}
              onChange={(rooms) => setForm((current) => ({ ...current, rooms }))}
              createItem={() => ({ name: "", size: "", photo: "" })}
            >
              {({ item, index, updateItem }) => <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}><TextField required fullWidth label={getMessage(locale.locale, "createPageRoomName")} value={item.name || ""} onChange={(event) => updateItem(index, "name", event.target.value)} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={getMessage(locale.locale, "createPageRoomSize")} value={item.size || ""} onChange={(event) => updateItem(index, "size", event.target.value)} /></Grid>
                <Grid size={12}><ImageUploadField label={getMessage(locale.locale, "createPageRoomPhoto")} value={item.photo || ""} onChange={(event) => updateItem(index, "photo", event.target.value)} messages={imageMessages(locale.locale)} /></Grid>
              </Grid>}
            </RepeatableSection>
            <RepeatableSection
              title={getMessage(locale.locale, "createPageEngineers")}
              addLabel={getMessage(locale.locale, "createPageAddEngineer")}
              emptyLabel={getMessage(locale.locale, "createPageEngineersEmpty")}
              removeLabel={getMessage(locale.locale, "createPageRemoveItem")}
              items={form.engineers}
              onChange={(engineers) => setForm((current) => ({ ...current, engineers }))}
              createItem={() => ({ id: "", name: "", role: "", avatar: "", instagram: "" })}
            >
              {({ item, index, updateItem }) => <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}><TextField required fullWidth label={getMessage(locale.locale, "createPageEngineerName")} value={item.name || ""} onChange={(event) => updateItem(index, "name", event.target.value)} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={getMessage(locale.locale, "createPageEngineerRole")} value={item.role || ""} onChange={(event) => updateItem(index, "role", event.target.value)} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><ImageUploadField label={getMessage(locale.locale, "createPageEngineerAvatar")} value={item.avatar || ""} onChange={(event) => updateItem(index, "avatar", event.target.value)} messages={imageMessages(locale.locale)} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth inputMode="url" label={getMessage(locale.locale, "createPageEngineerInstagram")} value={item.instagram || item.links?.instagram || ""} onChange={(event) => updateItem(index, "instagram", event.target.value)} /></Grid>
              </Grid>}
            </RepeatableSection>
            <RepeatableSection
              title={getMessage(locale.locale, "createPageRates")}
              addLabel={getMessage(locale.locale, "createPageAddRate")}
              emptyLabel={getMessage(locale.locale, "createPageRatesEmpty")}
              removeLabel={getMessage(locale.locale, "createPageRemoveItem")}
              items={form.rateCards}
              onChange={(rateCards) => setForm((current) => ({ ...current, rateCards }))}
              createItem={() => ({ label: "", price: "" })}
            >
              {({ item, index, updateItem }) => <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 8 }}><TextField required fullWidth label={getMessage(locale.locale, "createPageRateLabel")} value={item.label || ""} onChange={(event) => updateItem(index, "label", event.target.value)} /></Grid>
                <Grid size={{ xs: 12, sm: 4 }}><TextField required fullWidth type="number" inputProps={{ min: 0, step: "0.01" }} label={getMessage(locale.locale, "createPageRatePrice")} value={item.price ?? ""} onChange={(event) => updateItem(index, "price", event.target.value)} /></Grid>
              </Grid>}
            </RepeatableSection>
            <TextField multiline minRows={4} label={getMessage(locale.locale, "createPagePolicies")} value={form.studioPolicies} onChange={update("studioPolicies")} />
            <RepeatableSection
              title={getMessage(locale.locale, "createPagePortfolio")}
              addLabel={getMessage(locale.locale, "createPageAddPortfolio")}
              emptyLabel={getMessage(locale.locale, "createPagePortfolioEmpty")}
              removeLabel={getMessage(locale.locale, "createPageRemoveItem")}
              items={form.portfolio}
              onChange={(portfolio) => setForm((current) => ({ ...current, portfolio }))}
              createItem={() => ({ type: "audio", title: "", url: "", thumb: "" })}
            >
              {({ item, index, updateItem }) => <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 4 }}><TextField select fullWidth label={getMessage(locale.locale, "createPagePortfolioType")} value={item.type || "audio"} onChange={(event) => updateItem(index, "type", event.target.value)}><MenuItem value="audio">{getMessage(locale.locale, "createPagePortfolioAudio")}</MenuItem><MenuItem value="video">{getMessage(locale.locale, "createPagePortfolioVideo")}</MenuItem></TextField></Grid>
                <Grid size={{ xs: 12, sm: 8 }}><TextField required fullWidth label={getMessage(locale.locale, "createPagePortfolioTitle")} value={item.title || ""} onChange={(event) => updateItem(index, "title", event.target.value)} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><TextField required fullWidth inputMode="url" label={getMessage(locale.locale, "createPagePortfolioUrl")} value={item.url || ""} onChange={(event) => updateItem(index, "url", event.target.value)} /></Grid>
                <Grid size={{ xs: 12, sm: 6 }}><ImageUploadField label={getMessage(locale.locale, "createPagePortfolioThumb")} value={item.thumb || ""} onChange={(event) => updateItem(index, "thumb", event.target.value)} messages={imageMessages(locale.locale)} /></Grid>
              </Grid>}
            </RepeatableSection>
          </>}
          <Box id="availability" sx={{ scrollMarginTop: 88 }}>
            <AvailabilityEditor
              value={form.availability}
              onChange={(availability) => setForm((current) => ({ ...current, availability }))}
              locale={locale.locale}
              defaultTimeZone={locale.timeZone}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" disabled={status.busy}>{getMessage(locale.locale, status.busy ? "createPageSaving" : isEditing ? "editPageSave" : "createPageSave")}</Button>
            <Button type="button" onClick={() => navigate("/me")} disabled={status.busy}>{getMessage(locale.locale, "createPageCancel")}</Button>
          </Stack>
        </Stack>}
      </Paper>
    </FormShell>
  </>;
}

export function NotFound() {
  const navigate = useNavigate();
  return <Container maxWidth="sm" sx={{ minHeight: "100vh", display: "grid", placeItems: "center", textAlign: "center" }}><Box><Typography variant="overline" color="primary">404</Typography><Typography variant="h2" gutterBottom>That page missed the beat.</Typography><Typography color="text.secondary" sx={{ mb: 3 }}>The link may have moved, or the page is no longer published.</Typography><Button variant="contained" onClick={() => navigate("/")}>Back to discovery</Button></Box></Container>;
}
